import type { Apunte } from '../api/accounting';
import { datosLibro } from './libro';
import type { Comision } from '../api/commissions';

// Export del libro de ingresos y gastos para la gestoría. Dialecto Excel-ES:
// separador `;`, decimales con coma, fechas DD/MM/YYYY, BOM UTF-8 para que Excel
// respete los acentos. Todo en cliente (es el SPA del CRM, la descarga normal
// del navegador vale). Portado del patrón de csv.ts de maman-tax.

const nf = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const eur = (n: number) => nf.format(n || 0);

function fechaES(iso?: string): string {
  if (!iso || iso.length < 10) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

// «Total» es lo que pone la factura (base + IVA − retención). La retención de
// IRPF va en sus columnas y, al final, sumada por trimestre para el Modelo 111.
const COLS = [
  'Fecha', 'Trimestre', 'Tipo', 'Categoría', 'Concepto', 'Contraparte',
  'Base imponible', 'IVA %', 'IVA €', 'Retención IRPF %', 'Retención IRPF €', 'Total',
];

function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function fila(a: Apunte): string {
  return [
    fechaES(a.fecha),
    a.trimestre || '',
    a.tipo === 'ingreso' ? 'Ingreso' : 'Gasto',
    a.categoria || '',
    a.concepto || '',
    a.contraparte || '',
    eur(a.base_imponible ?? 0),
    a.iva_pct != null ? String(a.iva_pct) : '',
    eur(a.iva_amount ?? 0),
    a.irpf_pct ? String(a.irpf_pct) : '',
    a.irpf_amount ? eur(a.irpf_amount) : '',
    eur(a.total ?? 0),
  ]
    .map(csvCell)
    .join(';');
}

/** Construye el CSV del año: sólo apuntes `confirmed` + las comisiones, ingresos
 *  primero y por fecha, y al final el resumen por trimestre y del año (y lo
 *  retenido de IRPF, si hay algo). Devuelve la cadena (con BOM). */
export function construirLibroCsv(
  anio: string,
  apuntes: Apunte[],
  comisiones: Comision[],
): string {
  const { filas, trimestres, hayRetenciones } = datosLibro(anio, apuntes, comisiones);

  const lineas: string[] = [COLS.join(';')];
  for (const a of filas) lineas.push(fila(a));

  lineas.push('');
  lineas.push(['Resumen', anio].map(csvCell).join(';'));
  for (const r of trimestres) {
    lineas.push([r.t, '', '', '', '', 'Ingresos / Gastos / Resultado',
      eur(r.ingresos), '', eur(r.gastos), '', '', eur(r.ingresos - r.gastos)].map(csvCell).join(';'));
  }

  // Lo que Habitania ha retenido a sus proveedores autónomos y debe ingresar en
  // Hacienda cada trimestre (Modelo 111). Solo si hay alguna retención.
  if (hayRetenciones) {
    lineas.push('');
    lineas.push(['Retenciones de IRPF practicadas (Modelo 111)', anio].map(csvCell).join(';'));
    for (const r of trimestres) {
      lineas.push([r.t, '', '', '', '', 'Retenido', '', '', '', '', eur(r.retenido), ''].map(csvCell).join(';'));
    }
  }

  return '\ufeff' + lineas.join('\r\n');
}

/** Descarga el CSV como fichero (blob). */
export function descargarLibroCsv(
  anio: string,
  apuntes: Apunte[],
  comisiones: Comision[],
) {
  const csv = construirLibroCsv(anio, apuntes, comisiones);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `libro-habitania-${anio}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
